import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import type { Asset } from "features/catalog/model/asset.model";
import {
  addToWatchlist,
  getWatchlist,
  removeFromWatchlist,
  type WatchlistDto,
} from "../api/watchlist.api";
import { getCookie } from "shared/auth/utils/cookieUtils";
import toastStyles from "../components/WatchlistToast.module.css";

function parseUserIdFromCookie(): number {
  const raw = getCookie("userId");
  if (!raw) return 0;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? n : 0;
}

function resolveAssetUploadIdFromAsset(asset: Asset): number {
  if (typeof asset.assetUploadId === "number" && asset.assetUploadId > 0) {
    return asset.assetUploadId;
  }
  const parsed = Number.parseInt(asset.id, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

type WatchlistContextValue = {
  enabled: boolean;
  userId: number;
  items: WatchlistDto[];
  loading: boolean;
  refresh: () => Promise<void>;
  isInWatchlist: (catalogAssetId: string) => boolean;
  toggleWatchlist: (asset: Asset) => Promise<void>;
  removeByRowId: (rowId: number) => Promise<void>;
};

const noop = async () => {};

const defaultValue: WatchlistContextValue = {
  enabled: false,
  userId: 0,
  items: [],
  loading: false,
  refresh: noop,
  isInWatchlist: () => false,
  toggleWatchlist: noop,
  removeByRowId: noop,
};

const WatchlistContext = createContext<WatchlistContextValue>(defaultValue);

export function WatchlistProvider({
  enabled,
  children,
}: {
  enabled: boolean;
  children: React.ReactNode;
}) {
  const userId = useMemo(() => parseUserIdFromCookie(), []);
  const [items, setItems] = useState<WatchlistDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    variant: "success" | "error";
  } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((message: string, variant: "success" | "error") => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, variant });
    toastTimer.current = setTimeout(() => {
      setToast(null);
      toastTimer.current = null;
    }, 2800);
  }, []);

  const refresh = useCallback(async () => {
    if (!enabled || userId <= 0) return;
    setLoading(true);
    try {
      const list = await getWatchlist(userId);
      setItems(list);
    } catch {
      showToast("Could not load watchlist.", "error");
    } finally {
      setLoading(false);
    }
  }, [enabled, userId, showToast]);

  useEffect(() => {
    if (enabled && userId > 0) void refresh();
  }, [enabled, refresh, userId]);

  const isInWatchlist = useCallback(
    (catalogAssetId: string) =>
      items.some((r) => String(r.assetUploadId || r.id) === catalogAssetId),
    [items]
  );

  const toggleWatchlist = useCallback(
    async (asset: Asset) => {
      if (!enabled) return;
      const assetUploadId = resolveAssetUploadIdFromAsset(asset);
      if (assetUploadId <= 0) {
        showToast("Could not determine title id.", "error");
        return;
      }

      const existing = items.find(
        (row) => row.assetUploadId === assetUploadId || row.id === assetUploadId
      );
      const on = Boolean(existing);
      try {
        if (on) {
          const ok = await removeFromWatchlist(userId, { rowId: assetUploadId });
          if (ok) {
            setItems((prev) =>
              prev.filter(
                (r) =>
                  !(
                    (r.assetUploadId === assetUploadId || r.id === assetUploadId) &&
                    r.userId === userId
                  )
              )
            );
            showToast(`Removed “${asset.title}” from watchlist`, "success");
          } else {
            showToast("Could not remove from watchlist.", "error");
          }
        } else {
          const row = await addToWatchlist(userId, asset);
          if (row) {
            setItems((prev) => [...prev, row]);
            showToast(`Added “${asset.title}” to watchlist`, "success");
          } else {
            showToast("Already in your watchlist.", "success");
          }
        }
      } catch {
        showToast("Something went wrong. Try again.", "error");
      }
    },
    [enabled, userId, items, showToast]
  );

  const removeByRowId = useCallback(
    async (rowId: number) => {
      if (!enabled) return;
      try {
        const ok = await removeFromWatchlist(userId, { rowId });
        if (ok) {
          setItems((prev) =>
            prev.filter((r) => r.id !== rowId && r.assetUploadId !== rowId)
          );
          showToast("Removed from watchlist", "success");
        }
      } catch {
        showToast("Could not remove item.", "error");
      }
    },
    [enabled, userId, showToast]
  );

  const value = useMemo<WatchlistContextValue>(
    () =>
      enabled
        ? {
            enabled: true,
            userId,
            items,
            loading,
            refresh,
            isInWatchlist,
            toggleWatchlist,
            removeByRowId,
          }
        : defaultValue,
    [
      enabled,
      userId,
      items,
      loading,
      refresh,
      isInWatchlist,
      toggleWatchlist,
      removeByRowId,
    ]
  );

  const toastEl =
    toast && typeof document !== "undefined" ? (
      <div
        className={`${toastStyles.host} ${
          toast.variant === "success" ? toastStyles.success : toastStyles.error
        }`}
        role="status"
      >
        {toast.message}
      </div>
    ) : null;

  return (
    <WatchlistContext.Provider value={value}>
      {children}
      {toastEl ? createPortal(toastEl, document.body) : null}
    </WatchlistContext.Provider>
  );
}

export function useWatchlist(): WatchlistContextValue {
  return useContext(WatchlistContext);
}